/*###########################################################################
 // TCG Decks — index + editor (Alpine)
 //#########################################################################*/

import { showConfirm, showPrompt } from './_confirmDialog'
import { showSiteToast } from './_siteToast'

function readJson(id) {
  const el = document.getElementById(id)
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

async function postJson(url, body, csrfName, csrfToken) {
  const payload = new FormData()
  payload.append(csrfName, csrfToken)
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    payload.append(key, String(value))
  })

  const res = await fetch(url, {
    method: 'POST',
    body: payload,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${res.status})`)
  }
  return data
}

async function getJson(url) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed (${res.status})`)
  }
  return data
}

function withParams(base, params = {}) {
  const url = new URL(base, window.location.origin)
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    url.searchParams.set(key, String(value))
  })
  return url.toString()
}

function navigateTo(path) {
  const swup = window.swup
  if (swup?.navigate) {
    swup.navigate(path)
    return
  }
  window.location.href = path
}

export function registerDecks(Alpine) {
  Alpine.data('deckIndex', () => {
    const cfg = readJson('deck-index-config') || {}
    const favoritedById = {}
    ;(cfg.decksMeta || []).forEach((d) => {
      favoritedById[d.id] = Boolean(d.favorited)
    })

    return {
      deckCount: Number(cfg.deckCount) || 0,
      formatOptions: cfg.formatOptions || [],
      favoritedById,
      createOpen: false,
      creating: false,
      importOpen: false,
      importing: false,
      form: { name: '', format: 'standard' },
      importForm: { name: '', exportText: '' },

      openCreate() {
        this.form = { name: '', format: 'standard' }
        this.createOpen = true
      },
      closeCreate() {
        this.createOpen = false
      },
      openImport() {
        this.importForm = { name: '', exportText: '' }
        this.importOpen = true
      },
      closeImport() {
        this.importOpen = false
      },

      async submitCreate() {
        if (this.creating) return
        this.creating = true
        try {
          const data = await postJson(
            cfg.createUrl,
            { name: this.form.name, format: this.form.format },
            cfg.csrfName,
            cfg.csrfToken,
          )
          showSiteToast('Deck created')
          navigateTo(data.deck?.editUrl || `/account/decks/${data.deck.id}`)
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t create deck')
        } finally {
          this.creating = false
        }
      },

      async submitImport() {
        if (this.importing) return
        this.importing = true
        try {
          const data = await postJson(
            cfg.importUrl,
            {
              exportText: this.importForm.exportText,
              name: this.importForm.name || '',
              format: 'standard',
            },
            cfg.csrfName,
            cfg.csrfToken,
          )
          const unmatched = Array.isArray(data.unmatched) ? data.unmatched.length : 0
          if (unmatched) {
            showSiteToast(`Deck built — ${unmatched} line${unmatched === 1 ? '' : 's'} unmatched`)
          } else {
            showSiteToast('Deck imported')
          }
          if (data.justCompletedWalkthroughTask === 'importedFirstDeck') {
            window.dispatchEvent(
              new CustomEvent('app:walkthrough-task', { detail: { task: 'importedFirstDeck' } }),
            )
          }
          navigateTo(data.editUrl || data.deck?.editUrl || '/account/decks')
        } catch (err) {
          showSiteToast(err.message || 'Import failed')
        } finally {
          this.importing = false
        }
      },

      async toggleFavorite(deckId) {
        const next = !this.favoritedById[deckId]
        this.favoritedById[deckId] = next
        try {
          await postJson(
            cfg.toggleFavoriteUrl,
            { deckId, favorited: next ? '1' : '0' },
            cfg.csrfName,
            cfg.csrfToken,
          )
        } catch (err) {
          this.favoritedById[deckId] = !next
          showSiteToast(err.message || 'Couldn’t update favorite')
        }
      },

      async renameDeck(deckId, currentName) {
        const name = await showPrompt({
          title: 'Rename deck',
          message: 'Choose a new name for this deck.',
          defaultValue: currentName,
          confirmLabel: 'Rename',
        })
        if (name === null) return
        try {
          await postJson(
            cfg.renameUrl,
            { deckId, name },
            cfg.csrfName,
            cfg.csrfToken,
          )
          showSiteToast('Deck renamed')
          window.location.reload()
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t rename')
        }
      },

      async deleteDeck(deckId, name) {
        const ok = await showConfirm({
          title: 'Delete deck?',
          message: `Delete “${name}”? This can’t be undone.`,
          confirmLabel: 'Delete',
          danger: true,
        })
        if (!ok) return
        try {
          await postJson(cfg.deleteUrl, { deckId }, cfg.csrfName, cfg.csrfToken)
          showSiteToast('Deck deleted')
          const el = document.querySelector(`[data-deck-id="${deckId}"]`)
          el?.remove()
          this.deckCount = Math.max(0, this.deckCount - 1)
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t delete')
        }
      },
    }
  })

  Alpine.data('deckEditor', () => {
    const cfg = readJson('deck-editor-config') || {}
    const initial = cfg.deck || {
      name: '',
      format: 'standard',
      formatLabel: 'Standard',
      totalCards: 0,
      legal: true,
      validation: { errors: [], warnings: [], total: 0, legal: true },
      sections: { pokemon: [], trainer: [], energy: [] },
      cards: [],
    }

    return {
      deck: initial,
      formatOptions: cfg.formatOptions || [],
      urls: cfg.urls || {},
      busy: false,
      settingsOpen: false,
      settingsSaving: false,
      settingsForm: { name: '', format: 'standard' },
      cardModalOpen: false,
      activeCard: null,
      variantsMeta: null,
      variantOptions: [],
      variantsLoading: false,
      variantsError: '',
      irlOpen: false,
      pickerOpen: false,
      pickerStep: 'sets',
      pickerFilter: '',
      pickerLoading: false,
      pickerError: '',
      sets: cfg.sets || [],
      pickerCards: [],
      selectedSet: null,
      sectionOrder: [
        { key: 'pokemon', label: 'Pokémon' },
        { key: 'trainer', label: 'Trainer' },
        { key: 'energy', label: 'Energy' },
      ],
      sectionOpen: { pokemon: true, trainer: false, energy: false },

      init() {
        this.syncSectionOpen()
      },

      syncSectionOpen() {
        const order = ['pokemon', 'trainer', 'energy']
        let opened = false
        for (const key of order) {
          const has = (this.deck.sections?.[key] || []).length > 0
          this.sectionOpen[key] = has && !opened
          if (has && !opened) opened = true
        }
      },

      isSectionOpen(key) {
        return Boolean(this.sectionOpen[key])
      },

      toggleSection(key) {
        this.sectionOpen[key] = !this.sectionOpen[key]
      },

      get pickerTitle() {
        if (this.pickerStep === 'cards' && this.selectedSet) {
          return this.selectedSet.name
        }
        return 'Choose a set'
      },

      get filteredSets() {
        const q = this.pickerFilter.trim().toLowerCase()
        if (!q) return this.sets
        return this.sets.filter((s) => {
          const hay = `${s.name || ''} ${s.code || ''}`.toLowerCase()
          return hay.includes(q)
        })
      },

      get filteredPickerCards() {
        const q = this.pickerFilter.trim().toLowerCase()
        if (!q) return this.pickerCards
        return this.pickerCards.filter((c) => {
          const hay = `${c.name || ''} ${c.numberLabel || ''} ${c.cardNumber || ''}`.toLowerCase()
          return hay.includes(q)
        })
      },

      sectionCount(key) {
        const list = this.deck.sections?.[key] || []
        return list.reduce((sum, group) => sum + (Number(group.quantity) || 0), 0)
      },

      get irlSections() {
        return this.sectionOrder
          .map((section) => {
            const groups = this.deck.sections?.[section.key] || []
            // Walk name-groups so variant prints of the same card stay adjacent.
            const cards = []
            for (const group of groups) {
              for (const print of group.prints || []) {
                cards.push(print)
              }
            }
            return {
              key: section.key,
              label: section.label,
              cards,
            }
          })
          .filter((section) => section.cards.length > 0)
      },

      nameQuantity(name) {
        const key = String(name || '').trim().toLowerCase()
        if (!key) return 0
        let total = 0
        for (const section of Object.values(this.deck.sections || {})) {
          for (const group of section || []) {
            if (String(group.name || '').trim().toLowerCase() === key) {
              total += Number(group.quantity) || 0
            }
          }
        }
        return total
      },

      get validationStatusLabel() {
        if (!this.deck.legal) return 'Issues'
        if ((this.deck.validation?.warnings || []).length) return 'Notes'
        if (this.deck.totalCards === 60) return 'Ready'
        return 'In progress'
      },

      get validationSummary() {
        const total = Number(this.deck.totalCards) || 0
        const errors = this.deck.validation?.errors || []
        const warnings = this.deck.validation?.warnings || []
        if (errors.length) {
          return `${errors.length} construction issue${errors.length === 1 ? '' : 's'} · ${total}/60 cards`
        }
        if (warnings.length) {
          return `${warnings.length} note${warnings.length === 1 ? '' : 's'} · ${total}/60 cards`
        }
        if (total === 60) return 'All clear · 60/60 cards'
        if (total > 0) return `No issues yet · ${total}/60 cards`
        return 'Add cards to start the deck check.'
      },

      openSettings() {
        this.settingsForm = {
          name: this.deck.name || '',
          format: this.deck.format || 'standard',
        }
        this.settingsOpen = true
      },

      closeSettings() {
        this.settingsOpen = false
      },

      async saveSettings() {
        if (this.settingsSaving) return
        const name = String(this.settingsForm.name || '').trim()
        const format = this.settingsForm.format || 'standard'
        if (!name) {
          showSiteToast('Enter a deck name')
          return
        }

        this.settingsSaving = true
        try {
          if (name !== this.deck.name) {
            await postJson(
              this.urls.rename,
              { deckId: this.deck.id, name },
              cfg.csrfName,
              cfg.csrfToken,
            )
            this.deck.name = name
          }

          if (format !== this.deck.format) {
            const data = await postJson(
              this.urls.updateFormat,
              { deckId: this.deck.id, format },
              cfg.csrfName,
              cfg.csrfToken,
            )
            this.applyDeck(data.deck)
          } else {
            const opt = (this.formatOptions || []).find((o) => o.key === format)
            this.deck.format = format
            this.deck.formatLabel = opt?.label || this.deck.formatLabel
          }

          this.settingsOpen = false
          showSiteToast('Deck updated')
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t update deck')
        } finally {
          this.settingsSaving = false
        }
      },

      applyDeck(detail) {
        if (!detail) return
        const prevOpen = { ...this.sectionOpen }
        this.deck = detail
        // Keep accordion open state across quantity edits; only re-sync if a
        // section appeared/disappeared.
        for (const key of ['pokemon', 'trainer', 'energy']) {
          const has = (this.deck.sections?.[key] || []).length > 0
          if (!has) {
            this.sectionOpen[key] = false
          } else if (prevOpen[key] === undefined) {
            this.sectionOpen[key] = false
          } else {
            this.sectionOpen[key] = prevOpen[key]
          }
        }
        if (!Object.values(this.sectionOpen).some(Boolean)) {
          this.syncSectionOpen()
        }
      },

      async setQuantity(cardId, quantity) {
        if (this.busy) return
        this.busy = true
        try {
          const data = await postJson(
            this.urls.setQuantity,
            { deckId: this.deck.id, cardId, quantity },
            cfg.csrfName,
            cfg.csrfToken,
          )
          this.applyDeck(data.deck)
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t update card')
        } finally {
          this.busy = false
        }
      },

      adjustQty(card, delta) {
        const next = Math.max(0, (Number(card.quantity) || 0) + delta)
        this.setQuantity(card.cardId, next)
      },

      removeCard(card) {
        this.setQuantity(card.cardId, 0)
      },

      openIrlModal() {
        this.irlOpen = true
      },

      closeIrlModal() {
        this.irlOpen = false
      },

      adjustIrlQty(card, delta) {
        const max = Number(card.quantity) || 0
        const current = Number(card.irlQuantity) || 0
        const next = Math.max(0, Math.min(max, current + delta))
        if (next === current) return
        this.setIrlQuantity(card.cardId, next)
      },

      async setIrlQuantity(cardId, irlQuantity) {
        if (this.busy || !this.urls.setIrlQuantity) return
        this.busy = true
        try {
          const data = await postJson(
            this.urls.setIrlQuantity,
            { deckId: this.deck.id, cardId, irlQuantity },
            cfg.csrfName,
            cfg.csrfToken,
          )
          this.applyDeck(data.deck)
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t update IRL checklist')
        } finally {
          this.busy = false
        }
      },

      async chaseMissingIrl() {
        if (this.busy || !this.urls.chaseMissingIrl) return
        const missing = Number(this.deck.irlMissingCount) || 0
        if (missing < 1) {
          showSiteToast('Every print is already fully owned IRL')
          return
        }

        const listName = await showPrompt({
          title: 'Add missing prints to Chases',
          message: `Create a Chase list with the ${missing} print${missing === 1 ? '' : 's'} you still need from this deck.`,
          label: 'List name',
          initialValue: this.deck.name || '',
          confirmLabel: 'Create list',
        })
        if (listName === null) return

        this.busy = true
        try {
          const data = await postJson(
            this.urls.chaseMissingIrl,
            { deckId: this.deck.id, listName },
            cfg.csrfName,
            cfg.csrfToken,
          )
          if (data.deck) this.applyDeck(data.deck)
          const count = Number(data.cardCount) || missing
          const name = data.list?.name || listName
          showSiteToast(`Added ${count} card${count === 1 ? '' : 's'} to “${name}”`)
          this.irlOpen = false
          if (data.list?.url || this.urls.chases) {
            window.setTimeout(() => {
              navigateTo(data.list?.url || this.urls.chases)
            }, 450)
          }
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t create Chase list')
        } finally {
          this.busy = false
        }
      },

      openCardModal(card) {
        this.activeCard = { ...card }
        this.cardModalOpen = true
        this.variantsMeta = null
        this.variantOptions = []
        this.variantsError = ''
        this.loadVariants()
      },

      closeCardModal() {
        this.cardModalOpen = false
        this.activeCard = null
        this.variantsMeta = null
        this.variantOptions = []
        this.variantsError = ''
      },

      async loadVariants() {
        if (!this.activeCard?.cardId || !this.urls.nameVariants) return
        this.variantsLoading = true
        this.variantsError = ''
        try {
          const data = await getJson(
            withParams(this.urls.nameVariants, {
              deckId: this.deck.id,
              cardId: this.activeCard.cardId,
            }),
          )
          this.variantsMeta = {
            name: data.name,
            nameKey: data.nameKey,
            nameTotal: data.nameTotal,
            nameLimit: data.nameLimit,
            activeCardId: data.activeCardId,
            activeQuantity: data.activeQuantity,
            maxSwap: data.maxSwap,
          }
          this.variantOptions = Array.isArray(data.variants) ? data.variants : []
          const active = this.variantOptions.find((v) => v.isActive)
          if (active && this.activeCard) {
            this.activeCard.quantity = active.inDeck
          }
        } catch (err) {
          this.variantsError = err.message || 'Couldn’t load other prints.'
        } finally {
          this.variantsLoading = false
        }
      },

      async swapToVariant(variant) {
        if (!this.activeCard || !variant?.cardId || this.busy) return
        const qty = Number(variant.maxSwap) || 0
        if (qty < 1) {
          showSiteToast('Nothing left to swap from this print')
          return
        }
        this.busy = true
        try {
          const data = await postJson(
            this.urls.swapPrint,
            {
              deckId: this.deck.id,
              fromCardId: this.activeCard.cardId,
              toCardId: variant.cardId,
              quantity: qty,
            },
            cfg.csrfName,
            cfg.csrfToken,
          )
          this.applyDeck(data.deck)
          const next = (data.deck.cards || []).find((c) => c.cardId === variant.cardId)
          if (next) {
            this.activeCard = { ...next }
            await this.loadVariants()
            showSiteToast(`Swapped to ${variant.detailLabel || 'new print'}`)
          } else {
            this.closeCardModal()
            showSiteToast('Print swapped')
          }
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t swap print')
        } finally {
          this.busy = false
        }
      },

      async removeActiveCard() {
        if (!this.activeCard || this.busy) return
        const ok = await showConfirm({
          title: 'Remove print?',
          message: `Remove all ${this.activeCard.quantity}× “${this.activeCard.name}” (${this.activeCard.detailLabel || 'this print'}) from the deck?`,
          confirmLabel: 'Remove',
          danger: true,
        })
        if (!ok) return
        await this.setQuantity(this.activeCard.cardId, 0)
        this.closeCardModal()
        showSiteToast('Print removed')
      },

      async addActiveCardToChases() {
        const card = this.activeCard
        if (!card?.cardId || card.inChases || this.busy || !this.urls.addToChases) return
        this.busy = true
        try {
          const data = await postJson(
            this.urls.addToChases,
            { cardId: card.cardId },
            cfg.csrfName,
            cfg.csrfToken,
          )
          const userCardId = Number(data.userCardId) || null
          const chaseUrl = data.chaseUrl || (userCardId ? `/account/cards/${userCardId}` : null)
          this.activeCard = {
            ...this.activeCard,
            inChases: true,
            userCardId,
            chaseUrl,
            alertsEnabled: false,
            alertFavorited: false,
            alertMaxPrice: null,
          }
          // Keep list state in sync if this print is still on the deck.
          const live = (this.deck.cards || []).find((c) => c.cardId === card.cardId)
          if (live) {
            live.inChases = true
            live.userCardId = userCardId
            live.chaseUrl = chaseUrl
            live.alertsEnabled = false
            live.alertFavorited = false
            live.alertMaxPrice = null
          }
          showSiteToast(data.message || `Added ${card.name} to My Chases.`)
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t add to My Chases.')
        } finally {
          this.busy = false
        }
      },

      async confirmDelete() {
        const ok = await showConfirm({
          title: 'Delete deck?',
          message: `Delete “${this.deck.name}”? This can’t be undone.`,
          confirmLabel: 'Delete',
          danger: true,
        })
        if (!ok) return
        try {
          await postJson(
            this.urls.delete,
            { deckId: this.deck.id },
            cfg.csrfName,
            cfg.csrfToken,
          )
          showSiteToast('Deck deleted')
          navigateTo(this.urls.index || '/account/decks')
        } catch (err) {
          showSiteToast(err.message || 'Couldn’t delete')
        }
      },

      openPicker() {
        this.pickerOpen = true
        this.pickerStep = 'sets'
        this.pickerFilter = ''
        this.pickerError = ''
        this.selectedSet = null
        this.pickerCards = []
        if (!this.sets.length) this.loadSets()
      },

      closePicker() {
        this.pickerOpen = false
      },

      backPicker() {
        this.pickerStep = 'sets'
        this.pickerFilter = ''
        this.pickerCards = []
        this.selectedSet = null
      },

      async loadSets() {
        this.pickerLoading = true
        this.pickerError = ''
        try {
          const data = await getJson(this.urls.librarySets)
          this.sets = data.sets || []
        } catch (err) {
          this.pickerError = err.message || 'Couldn’t load sets.'
        } finally {
          this.pickerLoading = false
        }
      },

      async openSetCards(set) {
        this.selectedSet = set
        this.pickerStep = 'cards'
        this.pickerFilter = ''
        this.pickerLoading = true
        this.pickerError = ''
        try {
          const data = await getJson(
            withParams(this.urls.libraryCards, {
              setName: set.rawSetName ?? set.name,
              includeWatched: 1,
            }),
          )
          this.pickerCards = data.cards || []
        } catch (err) {
          this.pickerError = err.message || 'Couldn’t load cards.'
          this.pickerCards = []
        } finally {
          this.pickerLoading = false
        }
      },

      async addPickerCard(card) {
        const existing = (this.deck.cards || []).find((c) => c.cardId === card.id)
        const next = (existing?.quantity || 0) + 1
        const nameTotal = this.nameQuantity(card.name)
        const isBasic = /^Basic\s+/i.test(card.name || '') && /Energy/i.test(card.name || '')
        if (!isBasic && nameTotal >= 4) {
          showSiteToast(`At most 4 copies of “${card.name}”`)
          return
        }
        await this.setQuantity(card.id, next)
        showSiteToast(`Added ${card.name}`)
      },
    }
  })
}
